import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import type {
  ToggleCompleteResult,
  VisibilityChangedResult,
} from './useToggleInspectionTemplateVisibility';

export type UndoToastState = {
  isOpen: boolean;
  message: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
};

type UseTemplateLibraryUndoToastArgs = {
  /**
   * The list re-sync callback. Passed in (rather than closed over) so the undo
   * action always reconciles against the latest loaded templates — see
   * `useToggleInspectionTemplateVisibility`'s `undo` contract.
   */
  handleVisibilityChanged: (
    result: VisibilityChangedResult
  ) => Promise<void>;
};

type UseTemplateLibraryUndoToastResult = {
  undoToast: UndoToastState;
  closeUndoToast: () => void;
  showUndoToast: (completion: ToggleCompleteResult) => void;
};

/**
 * Owns the hide/unhide undo toast for the Template Library: its open state, the
 * direction-specific success copy, and the undo action (which re-runs the
 * visibility change in reverse and then shows a no-action confirmation toast so
 * it can't spawn an undo-the-undo loop).
 */
export function useTemplateLibraryUndoToast({
  handleVisibilityChanged,
}: UseTemplateLibraryUndoToastArgs): UseTemplateLibraryUndoToastResult {
  const { t } = useTranslation();

  const [undoToast, setUndoToast] = useState<UndoToastState>({
    isOpen: false,
    message: '',
  });

  const closeUndoToast = useCallback(
    () => setUndoToast(prev => ({ ...prev, isOpen: false })),
    []
  );

  const showUndoToast = useCallback(
    ({ templateName, hidden, undo }: ToggleCompleteResult) => {
      setUndoToast({
        isOpen: true,
        message: t(
          hidden
            ? 'web.admin.inspections.templateLibrary.visibility.hideSuccessToast'
            : 'web.admin.inspections.templateLibrary.visibility.unhideSuccessToast',
          { templateName }
        ),
        actionLabel: t('shared.common.undo'),
        onAction: () => {
          // Hand `undo` the current re-sync callback rather than one captured
          // earlier, so it reconciles against the latest loaded templates.
          undo(handleVisibilityChanged)
            .then(() => {
              // Post-undo confirmation toast: NO action (prevents an
              // undo-the-undo loop). `hidden` is the state BEFORE undo, so the
              // confirmation uses the reverse copy.
              setUndoToast({
                isOpen: true,
                message: t(
                  hidden
                    ? 'web.admin.inspections.templateLibrary.visibility.unhideSuccessToast'
                    : 'web.admin.inspections.templateLibrary.visibility.hideSuccessToast',
                  { templateName }
                ),
              });
            })
            .catch(() => {
              // undo() already showed the error toast; just close ours.
              closeUndoToast();
            });
        },
      });
    },
    [t, closeUndoToast, handleVisibilityChanged]
  );

  return { undoToast, closeUndoToast, showUndoToast };
}
